import { stripUndefined } from '@halo/helpers/clean-objects'
import DomainUserModel from './domain-user.model'
import * as deepEqual from 'react-fast-compare'
import { AccessRoleId } from '@halo/enums/access-role.enum'
import { AccessRole, AccessRoleMap } from '@halo/models/access-role'

export interface ICachedAccountUserRecord {
    id: number
    account_id: number
    user_id: number
    role_id: number
    role_name: string
    flags: string[]
}

type NewUserAccountUserRecord = Omit<ICachedAccountUserRecord, 'id' | 'userId'>

class AccountUserModel {
    public static fromCached(value: ICachedAccountUserRecord): AccountUserModel {
        return new AccountUserModel(value)
    }

    protected id: number
    protected account_id: number
    protected user_id: number
    protected role_id: number
    protected role_name: string
    protected flags: string[]
    protected domainRecords = new Map<number, DomainUserModel>()

    public constructor(value: ICachedAccountUserRecord | NewUserAccountUserRecord) {
        this.account_id = value.account_id
        this.role_id = value.role_id
        this.role_name = value.role_name
        this.flags = value.flags

        if ('id' in value) {
            this.id = value.id
        }
        if ('user_id' in value) {
            this.user_id = value.user_id
        }
    }

    public clone(): AccountUserModel {
        return new AccountUserModel(this.serialize())
    }

    public serialize(): ICachedAccountUserRecord {
        return stripUndefined({
            id: this.id,
            account_id: this.account_id,
            user_id: this.user_id,
            role_id: this.role_id,
            role_name: this.role_name,
            flags: this.flags,
        })
    }

    public matches(value: ICachedAccountUserRecord | AccountUserModel): boolean {
        const curr = this.serialize()
        const comparison = value instanceof AccountUserModel ? value.serialize() : value
        return deepEqual(curr, comparison)
    }

    public getId() {
        return this.id
    }
    public getAccountId() {
        return this.account_id
    }
    public getUserId() {
        return this.user_id
    }

    public setRole<T extends AccessRoleId>(role: AccessRole<T>) {
        this.role_id = role.id
        this.role_name = role.name
    }
    public getRole() {
        return AccessRoleMap.get(this.role_id)
    }

    public setRoleId(id: number) {
        this.role_id = id
    }
    public getRoleId() {
        return this.role_id
    }

    public setRoleName(name: string) {
        this.role_name = name
    }
    public getRoleName() {
        return this.role_name
    }

    public setFlags(flags: string[]) {
        this.flags = flags
    }
    public getFlags() {
        return this.flags
    }

    public setDomainRecords(records: ArrayLike<DomainUserModel>) {
        this.domainRecords = new Map<number, DomainUserModel>(
            Array.from(records).map((domain) => [domain.getDomainId(), domain]),
        )
    }

    public getDomainRecords() {
        return Object.freeze(this.domainRecords)
    }

    public setDomainRecord(id: number, record: DomainUserModel) {
        if (id !== record.getDomainId()) {
            throw new Error('Domain permissions ID set mismatch')
        }

        this.domainRecords.set(id, record)
    }

    public getDomainRecord(id: number) {
        return this.domainRecords.get(id)
    }

    public hasDomainRecord(id: number) {
        return this.domainRecords.has(id)
    }

    public deleteDomainRecord(id: number) {
        return this.domainRecords.delete(id)
    }

    public serializeDomainRecords() {
        return Array.from(this.domainRecords.values()).map((record) => record.serialize())
    }

    public serializeDomainRecord(id: number) {
        return this.domainRecords.get(id)?.serialize()
    }
}

export default AccountUserModel
